import { ref } from 'vue'
import type { SearchCriteria } from '@/api/search'
import { t } from '@/i18n'

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

// True only for an installed PWA — Web Push on iOS 16.4+ requires add-to-home-screen.
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function usePush() {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  const busy = ref(false)
  const error = ref('')

  // Request permission + obtain a push subscription. Returns the JSON form or null on failure.
  async function ensureSubscription(): Promise<PushSubscriptionJSON | null> {
    if (!supported) {
      error.value = t('push.notSupported')
      return null
    }
    // Only prompt on an explicit user gesture (callers are triggered by a click).
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      error.value = t('push.notAllowed')
      return null
    }

    const keyRes = await fetch('/api/push/vapidPublicKey')
    const { publicKey } = (await keyRes.json()) as { publicKey: string }
    if (!publicKey) {
      error.value = t('push.notConfigured')
      return null
    }

    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    })
    return sub.toJSON()
  }

  // siteId present → follow a specific site for weather push. Omitted → just register the
  // device (linked to the signed-in user), enabling favorite-rating notifications.
  async function subscribe(siteId?: string): Promise<boolean> {
    busy.value = true
    error.value = ''
    try {
      const json = await ensureSubscription()
      if (!json) return false
      const res = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, diveSiteIds: siteId ? [siteId] : [] }),
      })
      if (!res.ok) throw new Error(`subscribe → ${res.status}`)
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      return false
    } finally {
      busy.value = false
    }
  }

  // Save a criteria-based alert — push me when a site matching these filters appears.
  async function saveSearch(c: SearchCriteria): Promise<boolean> {
    busy.value = true
    error.value = ''
    try {
      const json = await ensureSubscription()
      if (!json) return false
      const res = await fetch('/api/push/searches', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          endpoint: json.endpoint,
          keys: json.keys,
          minDepth: c.minDepth,
          toilet: c.toilet,
          parking: c.parking,
          goodPast: c.goodPast,
        }),
      })
      if (!res.ok) throw new Error(`saveSearch → ${res.status}`)
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      return false
    } finally {
      busy.value = false
    }
  }

  // True if this device already has an active push subscription (permission granted).
  // Lets the UI remember that notifications are enabled across reloads.
  async function isSubscribed(): Promise<boolean> {
    if (!supported || Notification.permission !== 'granted') return false
    try {
      const reg = await navigator.serviceWorker.ready
      return (await reg.pushManager.getSubscription()) !== null
    } catch {
      return false
    }
  }

  // Remove this device's push subscription. Best-effort; the backend prunes the now-stale
  // row on its next send, so no server call is needed here. Returns whether it succeeded.
  async function unsubscribe(): Promise<boolean> {
    if (!supported) return false
    busy.value = true
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) await sub.unsubscribe()
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      return false
    } finally {
      busy.value = false
    }
  }

  return { supported, busy, error, subscribe, saveSearch, isSubscribed, unsubscribe }
}
